import { Sale, SaleItem, Payment, CartItem, ApiResponse, PaginatedApiResponse } from '../types';
import { apiClient } from './apiClient';

export interface CreateSaleRequest {
  customer_id?: number | null;
  customer_name?: string;
  add_customer?: boolean;
  customer_phone?: string;
  items: Array<{
    product_id?: number;
    product_name?: string;
    quantity: number;
    unit_price: number;
  }>;
  subtotal: number;
  tax_amount?: number;
  discount_amount?: number;
  total_amount: number;
  paid_amount: number;
  payment_method: 'cash' | 'bank_transfer' | 'other';
  payment_status: 'paid' | 'partial' | 'due';
  notes?: string;
}

export interface SaleFilters {
  search?: string;
  customer_id?: number;
  payment_status?: 'paid' | 'partial' | 'due';
  payment_method?: 'cash' | 'bank_transfer' | 'other';
  date_from?: string;
  date_to?: string;
  sortBy?: 'sale_date' | 'total_amount' | 'customer_name';
  sortOrder?: 'asc' | 'desc';
}

class SalesService {
  private baseUrl = '/sales';

  async createSale(saleData: CreateSaleRequest): Promise<Sale> {
    try {
      console.log('SalesService: Creating sale with data:', saleData);
      const response = await apiClient.post<ApiResponse<Sale>>(
        this.baseUrl, // Back to real endpoint
        saleData
      );

      console.log('SalesService: Sale creation response:', response);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to create sale');
    } catch (error: any) {
      console.error('SalesService: Sale creation error:', error);
      console.error('SalesService: Error response:', error.response?.data);
      console.error('SalesService: Error status:', error.response?.status);
      throw error;
    }
  }

  async getSales(
    filters: SaleFilters = {},
    page: number = 1,
    perPage: number = 10
  ): Promise<{
    data: Sale[];
    total: number;
    current_page: number;
    last_page: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', perPage.toString());
      
      // Add search parameter if provided
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      // Add payment_status filter
      if (filters.payment_status) {
        params.append('payment_status', filters.payment_status);
      }
      
      // Add payment_method filter
      if (filters.payment_method) {
        params.append('payment_method', filters.payment_method);
      }
      
      // Add date filters
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      
      // Add sorting
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }

      const response = await apiClient.get<PaginatedApiResponse<Sale[]>>(
        `${this.baseUrl}?${params}`
      );

      // apiClient.get() returns response.data, so response is actually the backend response body
      if (response.success && response.data) {
        // Backend returns { success: true, data: [...sales...], pagination: {...} }
        return {
          data: response.data,
          total: response.pagination?.total || 0,
          current_page: response.pagination?.current_page || 1,
          last_page: response.pagination?.last_page || 1
        };
      }

      throw new Error(response.message || 'Failed to fetch sales');
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error;
    }
  }

  async getSale(id: number): Promise<Sale & { items: SaleItem[] }> {
    try {
      const response = await apiClient.get<ApiResponse<Sale & { items: SaleItem[] }>>(
        `${this.baseUrl}/${id}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch sale');
    } catch (error) {
      console.error('Error fetching sale:', error);
      throw error;
    }
  }

  async addPayment(saleId: number, paymentData: {
    amount: number;
    payment_method: 'cash' | 'bank_transfer' | 'other';
    notes?: string;
  }): Promise<Payment> {
    try {
      const response = await apiClient.post<ApiResponse<Payment>>(
        `${this.baseUrl}/${saleId}/payments`,
        paymentData
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to add payment');
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  }

  async getSalePayments(saleId: number): Promise<Payment[]> {
    try {
      const response = await apiClient.get<ApiResponse<Payment[]>>(
        `${this.baseUrl}/${saleId}/payments`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch payments');
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  }

  async printReceipt(saleId: number): Promise<{
    receipt_data: string;
    print_url: string;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `${this.baseUrl}/${saleId}/receipt`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to generate receipt');
    } catch (error) {
      console.error('Error generating receipt:', error);
      throw error;
    }
  }

  async getDashboardStats(): Promise<{
    todaySales: number;
    weekSales: number;
    monthSales: number;
    totalDues: number;
    salesCount: number;
    topSellingProducts: Array<{
      product_name: string;
      total_quantity: number;
      total_amount: number;
    }>;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>('/dashboard/stats');

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch dashboard stats');
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  // Helper method to calculate cart totals
  calculateCartTotals(items: CartItem[], discount: number = 0, taxRate: number = 0) {
    // Ensure items is an array and filter out invalid items
    const validItems = Array.isArray(items) ? items.filter(item => 
      item && typeof item.total === 'number' && !isNaN(item.total)
    ) : [];
    
    const subtotal = validItems.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * taxRate) / 100;
    const total = taxableAmount + taxAmount;

    // Ensure all returned values are valid numbers
    return {
      subtotal: isNaN(subtotal) ? 0 : subtotal,
      discountAmount: isNaN(discountAmount) ? 0 : discountAmount,
      taxAmount: isNaN(taxAmount) ? 0 : taxAmount,
      total: isNaN(total) ? 0 : total,
    };
  }

  async getProductsSorted(params: {
    search?: string;
    sortBy?: 'most_sold' | 'recently_sold' | 'low_stock' | 'name';
    limit?: number;
  } = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const response = await apiClient.get<ApiResponse<any[]>>(
        `${this.baseUrl}/products/sorted?${queryParams.toString()}`
      );

      if (response.success && response.data) {
        return response;
      }

      throw new Error(response.message || 'Failed to fetch products');
    } catch (error) {
      console.error('Error fetching sorted products:', error);
      throw error;
    }
  }

  async getSalesStats() {
    try {
      const response = await apiClient.get<ApiResponse<any>>(
        `${this.baseUrl}/stats`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch sales stats');
    } catch (error) {
      console.error('Error fetching sales stats:', error);
      throw error;
    }
  }

  async updateSale(id: number, saleData: Partial<CreateSaleRequest>) {
    try {
      const response = await apiClient.put<ApiResponse<Sale>>(
        `${this.baseUrl}/${id}`,
        saleData
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to update sale');
    } catch (error) {
      console.error('Error updating sale:', error);
      throw error;
    }
  }

  async deleteSale(id: number) {
    try {
      console.log('deleteSale service calling API for id:', id);
      const response = await apiClient.delete<ApiResponse<void>>(
        `${this.baseUrl}/${id}`
      );
      
      console.log('Delete API response:', response);

      if (response.success) {
        console.log('Sale deleted successfully');
        return true;
      }

      const errorMsg = response.message || 'Failed to delete sale';
      console.error('Delete failed with message:', errorMsg);
      throw new Error(errorMsg);
    } catch (error: any) {
      console.error('Error deleting sale:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  async bulkDeleteSales(ids: number[]) {
    try {
      console.log('bulkDeleteSales service calling API with ids:', ids);
      const response = await apiClient.post<ApiResponse<{
        deleted_count: number;
        errors: string[];
      }>>(
        `${this.baseUrl}/bulk-delete`,
        { ids }
      );

      console.log('Bulk delete API response:', response);

      if (response.success) {
        console.log('Bulk delete successful');
        return response.data;
      }

      const errorMsg = response.message || 'Failed to delete sales';
      console.error('Bulk delete failed with message:', errorMsg);
      throw new Error(errorMsg);
    } catch (error: any) {
      console.error('Error bulk deleting sales:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }
}

export const salesService = new SalesService();